import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader } from "@/components/PageBits";
import { CodeBlock } from "@/components/CodeBlock";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API_ORIGIN = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
const BASE = `${API_ORIGIN}/api/v1`;

export default function QuickStart() {
  const { projectId, currentProject } = useWorkspace();
  const [keys, setKeys] = useState([]);
  useEffect(() => { if (projectId) api.get(`/projects/${projectId}/api-keys`).then((r) => setKeys(r.data)).catch(() => {}); }, [projectId]);
  const sampleKey = keys.find((k) => !k.revoked)?.public_id || "pk_xxx";

  const curlRoom = `curl -X POST ${BASE}/live/rooms \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"team-standup","type":"conference"}'`;

  const curlToken = `curl -X POST ${BASE}/live/tokens \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" \\
  -H "Content-Type: application/json" \\
  -d '{"room":"team-standup","identity":"alice","name":"Alice","role":"host"}'
# => { "token": "...", "ws_url": "wss://...", "expires_in": 900 }`;

  const nodeBackend = `// server.js — NEVER expose your API secret to the browser
import express from "express";
const app = express();
app.use(express.json());

const LIVECORE = "${BASE}";
const API_KEY = process.env.LIVECORE_API_KEY; // "pk_....<secret>"

// Your frontend calls THIS endpoint; it never sees the secret.
app.post("/get-token", async (req, res) => {
  const { room, identity, name } = req.body;
  const r = await fetch(\`\${LIVECORE}/live/tokens\`, {
    method: "POST",
    headers: { "X-API-Key": API_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ room, identity, name, role: "participant" }),
  });
  res.json(await r.json()); // { token, ws_url, expires_in }
});

app.listen(4000);`;

  const reactSdk = `// npm install @platform/live
import { useEffect, useRef } from "react";
import { LiveRoom } from "@platform/live";

export function Call({ room, identity, name }) {
  const containerRef = useRef(null);

  useEffect(() => {
    let live;
    (async () => {
      // fetch a short-lived token from YOUR backend
      const res = await fetch("/get-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ room, identity, name }),
      });
      const { token, ws_url } = await res.json();

      live = new LiveRoom({ container: containerRef.current });
      live.on("connected", () => console.log("live!"));
      live.on("participant-joined", (p) => console.log("joined", p.identity));
      await live.connect(ws_url, token);
      await live.enableCamera();
      await live.enableMicrophone();
    })();
    return () => live?.disconnect();
  }, [room, identity, name]);

  return <div ref={containerRef} style={{ display: "grid", gap: 8 }} />;
}`;

  const vanillaSdk = `// @platform/live is an npm package, not a hosted CDN asset.
// Install it in your Vite/Webpack/Rollup build first:
//   npm install @platform/live livekit-client
// Then use the same LiveRoom code as the React example from your bundled module.
// No fake cdn.example.com URL is provided by this platform.`;

  const callsApi = `# 1) Your backend starts a call (API secret, server-side only)
curl -X POST ${BASE}/calls \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" -H "Content-Type: application/json" \\
  -d '{"type":"video","caller":"alice","callees":["bob"]}'
# => { call: { id, status:"ringing" }, caller_token: { token, ws_url } }

# 2) Callee accepts -> gets a media token
curl -X POST ${BASE}/calls/<CALL_ID>/accept \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" -H "Content-Type: application/json" \\
  -d '{"identity":"bob","name":"Bob"}'

# Signaling states: ringing · accepted · ongoing · declined · busy · cancelled · missed · ended
# Your backend can subscribe over WS: ${BASE.replace(/^http/, "ws")}/calls/ws
# Supply X-API-Key: <PUBLIC.SECRET> as a WebSocket handshake header. Never put the secret in a URL.`;

  const callsSdk = `// Browser: connect the media leg with the token your backend returned
import { CallSession } from "@platform/live";

const call = new CallSession("video", { container: document.getElementById("stage") });
call.on("connected", () => console.log("in call"));
call.on("participant-joined", (p) => console.log(p.identity, "joined"));
await call.connect(callerToken); // { token, ws_url, expires_in }

await call.toggleCamera(true);
await call.startScreenShare();
await call.hangup();`;

  const meetingsApi = `# Instant meeting (becomes live immediately)
curl -X POST ${BASE}/meetings \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" -H "Content-Type: application/json" \\
  -d '{"title":"All-hands","type":"conference","max_participants":100}'

# Mint a token for an attendee with a role
curl -X POST ${BASE}/meetings/<MEETING_ID>/token \\
  -H "X-API-Key: ${sampleKey}.<SECRET>" -H "Content-Type: application/json" \\
  -d '{"identity":"carol","name":"Carol","role":"organizer"}'
# roles: organizer · cohost · participant · viewer`;

  return (
    <div data-testid="quickstart-page">
      <PageHeader title="Quick Start" description={`Go live in under 15 minutes${currentProject ? ` · ${currentProject.name}` : ""}.`} />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          ["1. Create an API key", "In API Keys, create a key with tokens:create. The secret is shown once."],
          ["2. Mint tokens server-side", "Your backend calls /live/tokens with the key. The secret never touches the browser."],
          ["3. Connect with the SDK", "Your frontend uses @platform/live with the short-lived token."],
        ].map(([t, d]) => (
          <div key={t} className="rounded-md border border-zinc-800 bg-zinc-900/30 p-4">
            <div className="text-sm font-medium text-zinc-100">{t}</div>
            <div className="mt-1 text-xs text-zinc-500">{d}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="curl" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="curl" data-testid="qs-tab-curl">cURL</TabsTrigger>
          <TabsTrigger value="node" data-testid="qs-tab-node">Node backend</TabsTrigger>
          <TabsTrigger value="react" data-testid="qs-tab-react">React</TabsTrigger>
          <TabsTrigger value="vanilla" data-testid="qs-tab-vanilla">Vanilla JS</TabsTrigger>
          <TabsTrigger value="calls" data-testid="qs-tab-calls">Calls</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="qs-tab-meetings">Meetings</TabsTrigger>
        </TabsList>
        <TabsContent value="curl" className="mt-4 space-y-4">
          <p className="text-sm text-zinc-400">Create a room and mint a participant token directly from your server.</p>
          <CodeBlock testId="qs-curl-room" language="bash · create room" code={curlRoom} />
          <CodeBlock testId="qs-curl-token" language="bash · mint token" code={curlToken} />
        </TabsContent>
        <TabsContent value="node" className="mt-4 space-y-4">
          <CodeBlock testId="qs-node" language="node.js" code={nodeBackend} />
        </TabsContent>
        <TabsContent value="react" className="mt-4 space-y-4">
          <CodeBlock testId="qs-react" language="tsx · @platform/live" code={reactSdk} />
        </TabsContent>
        <TabsContent value="vanilla" className="mt-4 space-y-4">
          <CodeBlock testId="qs-vanilla" language="html · @platform/live" code={vanillaSdk} />
        </TabsContent>
        <TabsContent value="calls" className="mt-4 space-y-4">
          <p className="text-sm text-zinc-400">1-to-1 and group calls with full signaling. Your backend orchestrates; the browser only gets a token.</p>
          <CodeBlock testId="qs-calls-api" language="bash · calls API" code={callsApi} />
          <CodeBlock testId="qs-calls-sdk" language="ts · CallSession" code={callsSdk} />
        </TabsContent>
        <TabsContent value="meetings" className="mt-4 space-y-4">
          <p className="text-sm text-zinc-400">Instant or scheduled meetings & webinars with organizer/cohost/participant/viewer roles.</p>
          <CodeBlock testId="qs-meetings-api" language="bash · meetings API" code={meetingsApi} />
        </TabsContent>
      </Tabs>

      <div className="mt-8 rounded-md border border-zinc-800 bg-zinc-900/30 p-4">
        <div className="text-sm text-zinc-300">Full API reference</div>
        <a href={`${API_ORIGIN}/api/docs`} target="_blank" rel="noreferrer"
          data-testid="swagger-link" className="mt-1 inline-block font-mono text-sm text-emerald-400 hover:underline">
          {API_ORIGIN || window.location.origin}/api/docs (OpenAPI / Swagger)
        </a>
      </div>
    </div>
  );
}
